import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Hidden } from '@material-ui/core';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { TcsmContentPropsState } from '../../../../global/models/epi-model';
import { useDependents } from '../../hooks/useDependents';
import { useSpouseInformation } from '../../hooks/useSpouseInfo';
import { RelationshipsProps } from '../../models/account-info-epi-props';
import { SpouseDisplay } from './components/spouse-display';
import { RelationshipsState, Spouse } from './relationships-model';
import { getCurrentDomesticPartner, getCurrentSpouse, getPreviousDomesticPartner, getPreviousSpouses } from './relationships-selectors';
import { DependentsDisplay } from './components/dependents-display';
import { DependentTypes, DependentsState } from '../dependents/dependents-model';
import { setPostDependentStatus, setPatchDependentStatus } from '../dependents/dependents-slice';
import { Person } from '../../../../global/models/person-model';
import { MaritalStatuses } from '../../../../global/constants/lists';
import { QuerySuccessMessage } from '../../../../global/components/messages/query-success-message';
import { FormTypes } from '../../models/form-models';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { buttonStyles } from '../../../../global/constants/theme/styles/components/buttonstyles';
import { ApiStatuses } from '../../../../global/models/api-model';
import { AccountInfoRoutes } from '../../constants/routes';
import { TcsmStates } from '../../../../root/tcsm-model';
import { DemographicAppKeys, DemographicsContext } from '../../../../global/models/demographics-model';
import { AESteps, AnnualEnrollmentContext, AnnualEnrollmentKey } from '../../../annual-enrollment/models/annual-enrollment-models';
import { AnnualEnrollmentProps, NewHireProps } from '../../../arc/models/arc-epi-props';
import { ButtonWrapper } from '../../../../global/components/layout/button-wrapper';
import { AnnualEnrollmentRoutes } from '../../../annual-enrollment/constants/annual-enrollment-routes';
import { fetchAnnualEnrollment } from '../../../annual-enrollment/helpers/annual-enrollment-requests';
import { setAEStep } from '../../../annual-enrollment/annual-enrollment-slice';
import { getGpsType } from '../../../../global/helpers/gps-helper';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { setCurrentNewHireBenefitEnrollmentStep } from '../../../arc/features/new-hire-enrollment/slice/new-hire-enrollment-slices';
import { BenefitEnrollmentStep } from '../../../arc/features/new-hire-enrollment/models/pension-enrollment-models';
import { setCurrentMyCpgMidYearStep } from '../mycpg-midyear/my-cpg-mid-year-slice';
import { GetNavigationButtonText, GetNextStepRedirect } from '../../helpers/my-relationship-helpers';
import { DomesticPartnerDisplay } from './components/domestic-partner-display';
import { QueryArrayMessage } from '../../../../global/components/messages/query-array-message';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../../arc/constants/routes';


interface Props {
	basePath?: string;
	isMember?: boolean;
	setNextStepCoverage?: boolean;
	customNextStep?: string;
}
export function MyRelationshipsApp(props: Props) {
	const app = useContext(DemographicsContext);
	const enrollmentContext = useContext(AnnualEnrollmentContext);
	const isNewHireEnrollment = enrollmentContext === AnnualEnrollmentKey.newHireEnrollment;
	const isAnnualEnrollment = enrollmentContext === AnnualEnrollmentKey.annualEnrollment || enrollmentContext === AnnualEnrollmentKey.participantEnrollment || enrollmentContext === AnnualEnrollmentKey.mycpgmidyear || enrollmentContext === AnnualEnrollmentKey.dioApproverEnrollment;
	const hasNavigationButtons = isAnnualEnrollment || isNewHireEnrollment;
	const currentPerson = useSelector<TcsmStates, Person>(state => state.personalInfo[app]?.currentPerson);
	const relationshipState = useSelector<TcsmStates, RelationshipsState>(state => state.relationships[app]);
	const dependentsState = useSelector<TcsmStates, DependentsState>(state => state.dependents[app]);
	const epiProps = useSelector<TcsmStates, TcsmContentPropsState<RelationshipsProps>>(state => state.epiProps?.relationshipsSettingState);
	const spousesLoaded = useSpouseInformation(currentPerson?.person_guid);
	const dependentsLoaded = useDependents(currentPerson?.person_guid);

	const newHireProps = useSelector<TcsmStates, NewHireProps>(state => state?.epiProps?.newHireSettingsState?.epiContent);

	const classes = buttonStyles();
	const dispatch = useDispatch();
	const routeMatch = useRouteMatch();
	const history = useHistory();
	const annualEnrollmentSettings = useSelector<TcsmStates, AnnualEnrollmentProps>(state => state.epiProps?.annualEnrollmentSettingsState?.epiContent);
	let gpsItem = getGpsType(isAnnualEnrollment ? GpsType.AE_RELATIONSHIP : GpsType.RELATIONSHIP, app);

	if (isNewHireEnrollment) {
		gpsItem = getGpsType(props.isMember ? GpsType.MEMBER_NEW_HIRE_RELATIONSHIP : GpsType.NEW_HIRE_RELATIONSHIP, app);
	}

	const groupId = useSelector<TcsmStates, number>(state => state?.groupsState?.currentGroup?.group_id);
	const isLifeEventCoverageForm = useSelector<TcsmStates, boolean>(state => state?.newHireEnrollmentState?.isLifeEventCoverageForm);
	const childDeathLink = app === DemographicAppKeys?.myCPG ? 
		useBasePath(`${AccountInfoRoutes.MyCpgRoot}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.DeathOfChild}`)
		: useBasePath(`${ArcRoutes.ArcRoot}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.DeathOfChild}`);

	useEffect(() => {
		dispatch(setPatchDependentStatus(ApiStatuses.None, app));
		dispatch(setPostDependentStatus(ApiStatuses.None, app));
	}, []);

	useEffect(() => {
		if (isAnnualEnrollment) {
			dispatch(setAEStep(AESteps.Relationships, app));
		}
		if (isNewHireEnrollment)
			dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.RELATIONSHIP));

		if (enrollmentContext === AnnualEnrollmentKey.mycpgmidyear)
			dispatch(setCurrentMyCpgMidYearStep(BenefitEnrollmentStep.RELATIONSHIP));

	}, []);

	if (!spousesLoaded || !dependentsLoaded) {
		return <LoadingCircle />;
	}

	const currentSpouse = getCurrentSpouse(relationshipState.spouses);
	const previousSpouses = getPreviousSpouses(relationshipState.spouses);
	const currentDomesticPartner = getCurrentDomesticPartner(dependentsState?.dependents?.domesticPartners);
	const previousDomesticPartner = getPreviousDomesticPartner(dependentsState?.dependents?.domesticPartners);

	const maritalStatusLink = `${routeMatch.path}${AccountInfoRoutes.MaritalStatusSegment}`;
	const relationshipPrevStep = `${props.basePath}${AccountInfoRoutes.PersonalInfoSegment}`;
	const relationshipNextStep = `${props.basePath}${AnnualEnrollmentRoutes.Coverage}`;
	const dioApproverNextStep = `${props.basePath}${AccountInfoRoutes.EmploymentSegment}`;
	const domesticPartnerLink = `${routeMatch.path}${AccountInfoRoutes.DomesticPartner}`;
	const prevStepOnClick = () => {
		if (enrollmentContext === AnnualEnrollmentKey.dioApproverEnrollment) {
			dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.PERSONAL_INFORMATION));
		}
		history.push(relationshipPrevStep);
	};
	const nextStepOnClick = () => {
		if (props?.customNextStep) {
			history.push(props?.customNextStep);
			return;
		}

		if (enrollmentContext === AnnualEnrollmentKey.newHireEnrollment) {
			history.push(GetNextStepRedirect(props?.isMember, props?.setNextStepCoverage, props?.basePath));
			return;
		}

		if (enrollmentContext === AnnualEnrollmentKey.participantEnrollment) {
			history.push(relationshipNextStep);
			return;
		}

		if (enrollmentContext === AnnualEnrollmentKey.dioApproverEnrollment) {
			if (isLifeEventCoverageForm) {
				dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.COVERAGE));
				history.push(relationshipNextStep);
				return;
			}
			dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.EMPLOYMENT));
			history.push(dioApproverNextStep);
			return;
		}
		fetchAnnualEnrollment(dispatch, app, currentPerson?.person_guid, true, groupId);
		history.push(relationshipNextStep);
	};

	const buttonTexts = GetNavigationButtonText(enrollmentContext, newHireProps, annualEnrollmentSettings, props.isMember, !!props?.customNextStep, isLifeEventCoverageForm);


	function RenderSpouseSection() {

		if (currentDomesticPartner && !(previousSpouses?.length > 0)) {
			return null;
		}

		return (
			<DividerSection sectionText={epiProps?.epiContent?.spousalHistoryHeader}>
				<ContentSpacer>
					<p>{epiProps?.epiContent?.currentSpouseLabel}</p>
					{currentSpouse != null
						? <SpouseDisplay
							canAddEdit={currentPerson.marital_status !== MaritalStatuses.Divorced && currentPerson.marital_status !== MaritalStatuses.Widowed}
							spouse={currentSpouse}
							basePath={routeMatch.path} />
						: <div dangerouslySetInnerHTML={{ __html: epiProps?.epiContent?.noCurrentSpouseFoundMessage }} />}
				</ContentSpacer>
				{
					(!currentDomesticPartner && !currentSpouse) &&
					<ContentSpacer>
						<Link className={classes.secondaryButton} to={maritalStatusLink}>{epiProps?.epiContent?.maritalStatusSectionLinkText}</Link>
					</ContentSpacer>
				}
				{previousSpouses?.length > 0 &&
					<>
						<p>{epiProps?.epiContent?.formerSpousesLabel}</p>
						{previousSpouses.map((s: Spouse, i: number) => {
							return (
								<SpouseDisplay
									key={i + s.client_nbr + s.end_date}
									spouse={s} canAddEdit={false}
									basePath={routeMatch.path} />
							);
						})}
					</>}
			</DividerSection>
		);
	}

	function RenderDomesticPartnerSection() {
		if (currentSpouse && !(previousDomesticPartner?.length > 0)) {
			return null;
		}

		return (
			<DividerSection removeDivider sectionText={epiProps?.epiContent?.domesticPartnerSectionTitle}>
				<ContentSpacer>
					{currentDomesticPartner != null
						?
						<>
							<p>{epiProps?.epiContent?.currentDomesticPartnerLabel}</p>
							<DomesticPartnerDisplay
								canAddEdit
								domesticPartner={currentDomesticPartner}
								basePath={routeMatch.path} />
						</>
						: <div dangerouslySetInnerHTML={{ __html: epiProps?.epiContent?.noDomesticPartnerMessage }} />}
				</ContentSpacer>
				{
					(!currentDomesticPartner && !currentSpouse) &&
					<ContentSpacer>
						<Link className={classes.secondaryButton} to={domesticPartnerLink} >{epiProps?.epiContent?.addDomesticPartnerButtonText}</Link>
					</ContentSpacer>
				}
				{
					previousDomesticPartner?.length > 0 &&
					<>
						<p>{epiProps?.epiContent?.formerDomesticPartnerLabel}</p>
						<DependentsDisplay
							dependents={previousDomesticPartner}
							noDependentsFoundMessage={epiProps?.epiContent?.noDependentsFoundMessage}
							basePath={routeMatch.path}
							dependentType={DependentTypes.domesticPartner}
						/>
					</>
				}
			</DividerSection>
		);
	}

	return (
		<>
			{
				isAnnualEnrollment && app === DemographicAppKeys.arc &&
				<ContentSpacer>
					<div dangerouslySetInnerHTML={{ __html: annualEnrollmentSettings?.adminAccessMessage }} />
				</ContentSpacer>
			}

			{
				isNewHireEnrollment && app === DemographicAppKeys.arc &&
				<ContentSpacer>
					<div dangerouslySetInnerHTML={{ __html: newHireProps?.adminAccessMessage }} />
				</ContentSpacer>
			}
			<QueryArrayMessage
				queryKey='success'
				useWarnings={[FormTypes.NEW_HIRE_SPOUSE_UPDATE_SUCCESS_WITH_WARNING, FormTypes.LIFE_EVENT_30_PLUS, FormTypes.IN_PROGRESS_MID_YEAR_WARNING, FormTypes.DOMESTIC_PARTNER_DEATH_WARNING, FormTypes.MAP_DOMESTIC_PARTNER_DEATH_WARNING, FormTypes.MARITAL_EVENT_30_PLUS, FormTypes.in_progress_mid_year_warning_marital_event]}
				codeToSkip={[FormTypes.MAP_PATCH_DEPENDENT, FormTypes.PATCH_DEPENDENT]}
			/>
			<QueryArrayMessage
				queryKey='warning'
				useWarnings={[FormTypes.HAVE_DOMESTIC_PARTNER_WARNING, FormTypes.HAVE_SPOUSE_WARNING]}
			/>
			<PageHeader
				headerText={epiProps?.epiContent?.myRelationshipsTitle}
			/>
			<Hidden mdUp>
				<SideBarContent>
					<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
				</SideBarContent>
			</Hidden>
			{
				RenderSpouseSection()
			}
			{
				RenderDomesticPartnerSection()
			}

			<DividerSection removeDivider sectionText={epiProps?.epiContent?.dependentsHeader}>
				<QuerySuccessMessage successfulAction={app === DemographicAppKeys.arc ? FormTypes.MAP_PATCH_DEPENDENT : FormTypes.PATCH_DEPENDENT} />
				<QuerySuccessMessage successfulAction={app === DemographicAppKeys.arc ? FormTypes.MAP_POST_DEPENDENT : FormTypes.POST_DEPENDENT} />
				<ContentSpacer>
					<DependentsDisplay
						dependents={dependentsState.dependents?.children}
						noDependentsFoundMessage={epiProps?.epiContent?.noDependentsFoundMessage}
						basePath={routeMatch.path}
						dependentType={DependentTypes.child}

					/>
					<Link className={classes.secondaryButton} to={`${routeMatch.path}${AccountInfoRoutes.DependentSegment}`}>{epiProps?.epiContent?.addDependentText}</Link>
				</ContentSpacer>
				{
					dependentsState.dependents?.children?.length > 0 && (
						<ContentSpacer>
							<button className='link-button' type='button' onClick={() => history.push(childDeathLink)}>{epiProps?.epiContent?.reportDeceasedChild}</button>
						</ContentSpacer>
					)
				}
			</DividerSection>

			{
				hasNavigationButtons &&
				<ButtonWrapper flexEnd>
					<button type='button' className={classes.tertiaryButton} onClick={prevStepOnClick}>{buttonTexts?.back}</button>
					<button type='button' className={classes.primaryButton} onClick={nextStepOnClick} >{buttonTexts?.continue}</button>
				</ButtonWrapper>
			}

		</>
	);
}

MyRelationshipsApp.defaultProps = {
	basePath: '',
	isMember: false,
	setNextStepCoverage: false,
	customNextStep: '',
};